import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import api from '@/services/api'
import type { UserSettings } from '@/types/UserSettings'

export const useUserSettingsStore = defineStore('userSettingsStore', () => {
    const settings = ref<UserSettings | null>(null)
    const isLoading = ref(false)
    const isFetched = ref(false)

    // Onboarding is complete when we've fetched settings and a defaultGroupId is set
    const isOnboardingComplete = computed(() =>
        isFetched.value && settings.value?.groupId != null
    )

    const fetchSettings = async () => {
        isLoading.value = true
        try {
            const response = await api.get<UserSettings>('/user/settings')
            settings.value = response.data
        } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status === 404) {
                settings.value = null
            } else {
                console.error('Failed to fetch user settings:', err)
            }
        } finally {
            isLoading.value = false
            isFetched.value = true
        }
    }

    const saveSettings = async (data: Partial<UserSettings>) => {
        const response = await api.put<UserSettings>('/user/settings', data)
        settings.value = response.data
    }

    return {
        settings,
        isLoading,
        isFetched,
        isOnboardingComplete,
        fetchSettings,
        saveSettings,
    }
})
